import React, { useContext, useMemo } from 'react';

export type ClipSelectDetailCardContextType = {
  /**
   * whether the video is playing
   */
  isPlaying: boolean;
  isPreviewReady: boolean | undefined;
  isEddyReady: boolean | undefined;
  /**
   * becomes true when isPreviewReady and isEddyReady become true for the first time.
   * after that, playback logic should keep the two in sync
   */
  isInitialized: boolean;
  /**
   * id of the suggestion that is loaded into the detail view
   */
  suggestionId: number;
};

export type ClipSelectDetailCardProviderProps = React.PropsWithChildren<
  ClipSelectDetailCardContextType
>;

const ClipSelectDetailCardContext = React.createContext<
  ClipSelectDetailCardContextType | undefined
>(undefined);

export const ClipSelectDetailCardProvider = ({
  children,
  isInitialized,
  isPlaying,
  isPreviewReady,
  isEddyReady,
  suggestionId,
}: ClipSelectDetailCardProviderProps) => {
  return (
    <ClipSelectDetailCardContext.Provider
      value={useMemo(
        () => ({
          isInitialized,
          isPlaying,
          isPreviewReady,
          isEddyReady,
          suggestionId,
        }),
        [isPlaying, isPreviewReady, isEddyReady, isInitialized, suggestionId],
      )}
    >
      {children}
    </ClipSelectDetailCardContext.Provider>
  );
};

export function useClipSelectDetailCardContext() {
  const context = useContext(ClipSelectDetailCardContext);

  if (!context) {
    throw new Error(
      'useClipSelectDetailCardContext must be used within a ClipSelectDetailCardProvider',
    );
  }

  return context;
}
